import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';

const CartContext = createContext(null);

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}

export function CartProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  // Track auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setCartItems([]);
        setPurchases([]);
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  // Real-time listener on users/{uid}/cart
  useEffect(() => {
    if (!currentUser) return;
    const cartRef = collection(db, 'users', currentUser.uid, 'cart');
    const unsub = onSnapshot(cartRef, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCartItems(items);
      setLoading(false);
    });
    return unsub;
  }, [currentUser]);

  // Real-time listener on users/{uid}/purchases
  useEffect(() => {
    if (!currentUser) return;
    const purchasesRef = collection(db, 'users', currentUser.uid, 'purchases');
    const unsub = onSnapshot(purchasesRef, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPurchases(items);
    });
    return unsub;
  }, [currentUser]);

  const addToCart = useCallback(
    async (project) => {
      if (!currentUser) return;
      // Don't add duplicates
      const exists = cartItems.some((i) => i.projectId === project.projectId);
      if (exists) return;

      const cartRef = collection(db, 'users', currentUser.uid, 'cart');
      await addDoc(cartRef, {
        projectId: project.projectId,
        name: project.name,
        price: project.price || 0,
        currency: project.currency || 'usd',
        imageUrl: project.imageUrl || project.imageUrls?.[0] || null,
        folderName: project.folderName,
        ownerId: project.ownerId,
        addedAt: serverTimestamp(),
      });
    },
    [currentUser, cartItems]
  );

  const removeFromCart = useCallback(
    async (projectId) => {
      if (!currentUser) return;
      const cartRef = collection(db, 'users', currentUser.uid, 'cart');
      const q = query(cartRef, where('projectId', '==', projectId));
      const snap = await getDocs(q);
      const deletions = snap.docs.map((d) => deleteDoc(doc(db, 'users', currentUser.uid, 'cart', d.id)));
      await Promise.all(deletions);
    },
    [currentUser]
  );

  const clearCart = useCallback(async () => {
    if (!currentUser) return;
    const cartRef = collection(db, 'users', currentUser.uid, 'cart');
    const snap = await getDocs(cartRef);
    const deletions = snap.docs.map((d) => deleteDoc(doc(db, 'users', currentUser.uid, 'cart', d.id)));
    await Promise.all(deletions);
  }, [currentUser]);

  const isInCart = useCallback(
    (projectId) => cartItems.some((i) => i.projectId === projectId),
    [cartItems]
  );

  const hasPurchased = useCallback(
    (projectId) => purchases.some((p) => p.projectId === projectId),
    [purchases]
  );

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price || 0), 0);

  const value = {
    cartItems,
    purchases,
    cartCount: cartItems.length,
    cartTotal,
    loading,
    currentUser,
    addToCart,
    removeFromCart,
    clearCart,
    isInCart,
    hasPurchased,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
